import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Users, Search, Filter, ChevronRight, ChevronLeft, Star, ArrowLeft, ExternalLink, BarChart3, TrendingUp, Briefcase, DollarSign, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const INDUSTRIES = ['Automotive', 'Medical', 'Real Estate', 'Tech', 'Finance', 'Legal', 'Insurance', 'Construction', 'Dental', 'Retail', 'Marketing/Media', 'Manufacturing', 'Food & Hospitality', 'Agriculture', 'Energy', 'Logistics', 'Education', 'Other'];
const STATUSES = ['prospect', 'active', 'inactive', 'suspended'];
const TIERS = ['standard', 'verified', 'elite'];

function TierBadge({ tier }) {
  if (tier === 'elite') return <span className="flex items-center gap-1 text-[10px] font-mono text-[#FF4343]"><Star className="w-3 h-3 fill-[#FF4343]" />Elite</span>;
  if (tier === 'verified') return <span className="text-[10px] font-mono text-yellow-400">Verified</span>;
  return <span className="text-[10px] font-mono text-[#6B7280]">Standard</span>;
}

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

// ==================== EXECUTIVE DATABASE TAB ====================
export function ExecutiveDatabaseTab() {
  const [data, setData] = useState({ executives: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ network_status: '', industry: '', tier: '', source: '' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchExecs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 30 });
      if (filters.network_status) params.append('network_status', filters.network_status);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.source) params.append('source', filters.source);
      const { data: d } = await axios.get(`${API}/executives?${params}`, { headers: getAuthHeaders() });
      setData(d);
    } catch {} finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchExecs(); }, [fetchExecs]);

  const openProfile = async (exec) => {
    setProfileLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/executives/${exec.id}`, { headers: getAuthHeaders() });
      setSelected(d);
    } catch {} finally { setProfileLoading(false); }
  };

  if (selected) {
    return <ExecutiveProfile exec={selected} onBack={() => { setSelected(null); fetchExecs(); }} />;
  }

  return (
    <div data-testid="exec-database-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={filters.network_status} onChange={e => { setFilters({...filters, network_status: e.target.value}); setPage(1); }}
          className="text-xs px-3 py-1.5 bg-[#090E33] border border-white/10 text-white focus:outline-none focus:border-[#FF4343]" data-testid="filter-status">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.industry} onChange={e => { setFilters({...filters, industry: e.target.value}); setPage(1); }}
          className="text-xs px-3 py-1.5 bg-[#090E33] border border-white/10 text-white focus:outline-none focus:border-[#FF4343]" data-testid="filter-industry">
          <option value="">All Industries</option>
          {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={filters.tier} onChange={e => { setFilters({...filters, tier: e.target.value}); setPage(1); }}
          className="text-xs px-3 py-1.5 bg-[#090E33] border border-white/10 text-white focus:outline-none focus:border-[#FF4343]" data-testid="filter-tier">
          <option value="">All Tiers</option>
          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.source} onChange={e => { setFilters({...filters, source: e.target.value}); setPage(1); }}
          className="text-xs px-3 py-1.5 bg-[#090E33] border border-white/10 text-white focus:outline-none focus:border-[#FF4343]">
          <option value="">All Sources</option>
          {['apollo', 'referral', 'inbound', 'manual'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-[10px] font-mono text-[#6B7280] self-center">{data.total} executives</span>
      </div>

      {/* Table */}
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#FF4343] animate-spin" /></div> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="exec-db-table">
              <thead>
                <tr className="border-b border-white/10">
                  {['Tier','Name','Title + Company','Industries','Status','Campaigns','Avg Score','Impressions','Source',''].map(h => (
                    <th key={h} className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.executives.map(ex => (
                  <tr key={ex.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => openProfile(ex)} data-testid={`exec-row-${ex.id}`}>
                    <td className="py-2.5"><TierBadge tier={ex.tier} /></td>
                    <td className="py-2.5 text-white text-xs font-medium">{ex.name}</td>
                    <td className="py-2.5 text-[#9CA3AF] text-xs max-w-[200px] truncate">{ex.title}{ex.company ? ` · ${ex.company}` : ''}</td>
                    <td className="py-2.5"><div className="flex gap-1 flex-wrap">{(ex.industries || []).map(i => <span key={i} className="text-[9px] px-1.5 py-0.5 bg-white/5 text-[#6B7280]">{i}</span>)}</div></td>
                    <td className="py-2.5"><span className={`text-[10px] font-mono uppercase ${ex.network_status === 'active' ? 'text-[#10B981]' : ex.network_status === 'suspended' ? 'text-[#FF4343]' : 'text-[#6B7280]'}`}>{ex.network_status || 'prospect'}</span></td>
                    <td className="py-2.5 font-mono text-xs text-[#6B7280]">{ex.total_campaigns || 0}</td>
                    <td className="py-2.5 font-mono text-xs text-[#6B7280]">{ex.avg_post_score ? ex.avg_post_score.toFixed(1) : '—'}</td>
                    <td className="py-2.5 font-mono text-xs text-[#6B7280]">{formatNum(ex.total_impressions)}</td>
                    <td className="py-2.5 text-[10px] text-[#6B7280] font-mono">{ex.source || '—'}</td>
                    <td className="py-2.5"><ChevronRight className="w-4 h-4 text-[#6B7280]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="text-xs px-3 py-1 border border-white/10 text-[#9CA3AF] disabled:opacity-30"><ChevronLeft className="w-3 h-3" /></button>
              <span className="text-xs text-[#6B7280] font-mono">{page} / {data.pages}</span>
              <button onClick={() => setPage(Math.min(data.pages, page + 1))} disabled={page >= data.pages} className="text-xs px-3 py-1 border border-white/10 text-[#9CA3AF] disabled:opacity-30"><ChevronRight className="w-3 h-3" /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== EXECUTIVE PROFILE ====================
function ExecutiveProfile({ exec, onBack }) {
  const [notes, setNotes] = useState(exec.notes || '');
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState(exec.network_status || 'prospect');
  const [offerModal, setOfferModal] = useState(false);
  const [offerCampaigns, setOfferCampaigns] = useState([]);
  const [offerForm, setOfferForm] = useState({ campaign_id: '', message: '', bespoke: false, custom_flat_fee: '', custom_cpm_rate: '', custom_bonus: '', send_linkedin_dm: false });
  const [sendingOffer, setSendingOffer] = useState(false);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/executives/${exec.id}`, { notes }, { headers: getAuthHeaders() });
    } catch {} finally { setSaving(false); }
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/executives/${exec.id}`, { network_status: status }, { headers: getAuthHeaders() });
      setEditStatus(status);
    } catch {}
  };

  const openOfferModal = async () => {
    try {
      const { data } = await axios.get(`${API}/campaigns?status=active`, { headers: getAuthHeaders() });
      setOfferCampaigns(data);
    } catch {}
    setOfferModal(true);
  };

  const sendOffer = async () => {
    if (!offerForm.campaign_id) return;
    setSendingOffer(true);
    try {
      const endpoint = offerForm.bespoke ? `${API}/campaigns/bespoke-offer` : `${API}/campaigns/${offerForm.campaign_id}/send-offer`;
      const body = offerForm.bespoke
        ? { executive_id: exec.id, campaign_id: offerForm.campaign_id, message: offerForm.message, custom_flat_fee: offerForm.custom_flat_fee ? parseFloat(offerForm.custom_flat_fee) : null, custom_cpm_rate: offerForm.custom_cpm_rate ? parseFloat(offerForm.custom_cpm_rate) : null, custom_bonus: offerForm.custom_bonus ? parseFloat(offerForm.custom_bonus) : null, send_linkedin_dm: offerForm.send_linkedin_dm }
        : { executive_id: exec.id, message: offerForm.message, send_linkedin_dm: offerForm.send_linkedin_dm };
      await axios.post(endpoint, body, { headers: getAuthHeaders() });
      setOfferModal(false);
      setOfferForm({ campaign_id: '', message: '', bespoke: false, custom_flat_fee: '', custom_cpm_rate: '', custom_bonus: '', send_linkedin_dm: false });
      alert('Offer sent successfully!');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to send offer'); }
    finally { setSendingOffer(false); }
  };

  const tierCriteria = exec.tier === 'elite' ? 'Max tier reached' :
    exec.tier === 'verified' ? 'Complete 5 more campaigns with avg 8.5+ and 500K+ impressions to reach Elite' :
    'Complete 3 campaigns with avg score 7.0+ and 50K+ impressions to reach Verified';

  return (
    <div data-testid="exec-profile">
      <button onClick={onBack} className="flex items-center gap-2 text-[#9CA3AF] hover:text-white text-xs mb-6 transition-colors" data-testid="profile-back">
        <ArrowLeft className="w-4 h-4" /> Back to Database
      </button>

      {/* Identity */}
      <div className="bg-[#111827] border border-white/10 p-6 mb-6 border-l-[3px] border-l-[#FF4343]">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="font-display text-2xl font-bold">{exec.name}</h2>
              <TierBadge tier={exec.tier} />
            </div>
            <p className="text-[#9CA3AF] text-sm">{exec.title}{exec.company ? ` · ${exec.company}` : ''}</p>
            <p className="text-[10px] text-[#6B7280] mt-1">{exec.city}{exec.state ? `, ${exec.state}` : ''}</p>
          </div>
          <div className="flex gap-2">
            {exec.linkedin_url && <a href={exec.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 border border-white/10 text-[#9CA3AF] hover:text-white transition-colors">LinkedIn <ExternalLink className="w-3 h-3 inline" /></a>}
          </div>
        </div>
      </div>

      {/* Network Status */}
      <div className="flex gap-2 mb-6">
        {STATUSES.map(s => (
          <button key={s} onClick={() => updateStatus(s)} data-testid={`status-${s}`}
            className={`text-xs px-3 py-1.5 border transition-colors capitalize ${editStatus === s ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280] hover:text-white'}`}>{s}</button>
        ))}
      </div>

      {/* Send Offer Button */}
      <div className="flex gap-2 mb-6">
        <button onClick={openOfferModal} data-testid="send-offer-btn"
          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
          Send Campaign Offer
        </button>
        <button onClick={() => { setOfferForm(f => ({ ...f, bespoke: true })); openOfferModal(); }} data-testid="bespoke-offer-btn"
          className="text-xs px-4 py-2 border border-[#FF4343]/50 text-[#FF4343] hover:bg-[#FF4343]/10 transition-all">
          Bespoke Whale Offer
        </button>
      </div>

      {/* Offer Modal */}
      {offerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOfferModal(false)} />
          <div className="relative glass max-w-md w-full p-6 max-h-[80vh] overflow-y-auto" data-testid="offer-modal">
            <h3 className="font-display text-lg font-bold mb-4">
              {offerForm.bespoke ? 'Bespoke Offer' : 'Send Offer'} to <span className="text-[#FF4343]">{exec.name}</span>
            </h3>
            <div className="space-y-3">
              <select value={offerForm.campaign_id} onChange={e => setOfferForm({...offerForm, campaign_id: e.target.value})} data-testid="offer-campaign-select"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                <option value="">Select Campaign</option>
                {offerCampaigns.map(c => <option key={c.id} value={c.id}>{c.title} ({c.brand_name})</option>)}
              </select>
              <textarea value={offerForm.message} onChange={e => setOfferForm({...offerForm, message: e.target.value})} placeholder="Personal message to the executive..." rows={3} data-testid="offer-message"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
              {exec.linkedin_url && (
                <label className="flex items-center gap-2 cursor-pointer mt-1" data-testid="linkedin-dm-toggle">
                  <input type="checkbox" checked={offerForm.send_linkedin_dm} onChange={e => setOfferForm({...offerForm, send_linkedin_dm: e.target.checked})}
                    className="w-3.5 h-3.5 accent-[#0A66C2]" />
                  <span className="text-xs text-[#9CA3AF]">Also send via <span className="text-[#0A66C2] font-medium">LinkedIn DM</span></span>
                </label>
              )}
              {offerForm.bespoke && (
                <div className="space-y-2 border-t border-white/10 pt-3">
                  <p className="text-[10px] text-[#FF4343] uppercase font-semibold">Custom Compensation</p>
                  <input type="number" value={offerForm.custom_flat_fee} onChange={e => setOfferForm({...offerForm, custom_flat_fee: e.target.value})} placeholder="Custom Flat Fee ($)" data-testid="offer-flat-fee"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                  <input type="number" value={offerForm.custom_cpm_rate} onChange={e => setOfferForm({...offerForm, custom_cpm_rate: e.target.value})} placeholder="Custom CPM Rate ($)" data-testid="offer-cpm"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                  <input type="number" value={offerForm.custom_bonus} onChange={e => setOfferForm({...offerForm, custom_bonus: e.target.value})} placeholder="Custom Bonus ($)" data-testid="offer-bonus"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={sendOffer} disabled={sendingOffer || !offerForm.campaign_id} data-testid="send-offer-confirm"
                  className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
                  {sendingOffer ? 'Sending...' : 'Send Offer'}
                </button>
                <button onClick={() => setOfferModal(false)} className="px-4 border border-white/20 text-[#9CA3AF] py-2 text-xs hover:text-white transition-all">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Campaigns', value: exec.total_campaigns || 0, color: 'text-white' },
          { label: 'Posts', value: exec.total_posts || 0, color: 'text-white' },
          { label: 'Impressions', value: formatNum(exec.total_impressions), color: 'text-[#FF4343]' },
          { label: 'Avg Score', value: exec.avg_post_score ? exec.avg_post_score.toFixed(1) : '—', color: 'text-[#10B981]' },
          { label: 'AI Score', value: exec.ai_score || '—', color: 'text-[#FF4343]' },
        ].map(s => (
          <div key={s.label} className="bg-[#111827] border border-white/10 p-3 sm:p-4 text-center min-w-0">
            <span className={`block font-mono text-lg sm:text-2xl font-bold tabular-nums leading-tight truncate ${s.color}`}>{s.value}</span>
            <p className="text-[9px] text-[#6B7280] font-mono uppercase tracking-wider mt-1 truncate">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tier Progress */}
      <div className="bg-[#111827] border border-white/10 p-4 mb-6">
        <p className="text-xs text-[#9CA3AF]"><TierBadge tier={exec.tier} /> <span className="ml-2 text-[10px] text-[#6B7280]">{tierCriteria}</span></p>
      </div>

      {/* Campaign History */}
      {exec.campaign_history?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3">Campaign History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10">
                {['Campaign','Brand','Platform','Impressions','Score','Payout','Status'].map(h => <th key={h} className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">{h}</th>)}
              </tr></thead>
              <tbody>
                {exec.campaign_history.map(p => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="py-2 text-white text-xs">{p.campaign_title || p.campaign_id}</td>
                    <td className="py-2 text-[#9CA3AF] text-xs">{p.brand_name || '—'}</td>
                    <td className="py-2 text-[#6B7280] text-xs">{p.post_platform || '—'}</td>
                    <td className="py-2 font-mono text-xs text-[#6B7280]">{formatNum(p.impressions)}</td>
                    <td className="py-2 font-mono text-xs text-[#6B7280]">{p.ai_post_score || '—'}</td>
                    <td className="py-2 font-mono text-xs text-[#FF4343]">{p.payout_amount ? `$${p.payout_amount}` : '—'}</td>
                    <td className="py-2"><span className={`text-[10px] font-mono uppercase ${p.status === 'completed' || p.status === 'paid' ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Outreach History */}
      {exec.outreach_history?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3">Outreach History</h3>
          <div className="space-y-1.5">
            {exec.outreach_history.map(o => (
              <div key={o.id} className="flex items-center gap-3 bg-[#111827] border border-white/10 px-3 py-2 text-xs">
                <span className="font-mono text-[#6B7280] text-[10px]">{o.sequence}-{o.step}</span>
                <span className="text-[#6B7280] capitalize">{o.channel}</span>
                <span className="text-[#6B7280]">{o.sent_at ? new Date(o.sent_at).toLocaleDateString() : '—'}</span>
                {o.opened && <CheckCircle className="w-3 h-3 text-[#10B981]" />}
                {o.clicked && <span className="text-[10px] text-[#10B981]">Clicked</span>}
                {o.high_intent && <span className="text-[10px] font-mono text-[#FF4343] bg-[#FF4343]/10 px-1.5 py-0.5">HIGH INTENT</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="bg-[#111827] border border-white/10 p-4">
        <h3 className="text-sm font-semibold mb-2">Admin Notes</h3>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
          placeholder="e.g., prefers LinkedIn only, do not contact for pharma campaigns..."
          data-testid="exec-notes"
          className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-2" />
        <button onClick={saveNotes} disabled={saving} data-testid="save-notes-btn"
          className="text-[10px] px-3 py-1 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-white transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Notes'}
        </button>
      </div>
    </div>
  );
}

// ==================== CAMPAIGN BUILDER ====================
const SETUP_TIERS = [
  { key: 'standard', label: 'Standard', price: '$500', desc: '25 executives · Single vertical + geography · Outreach sequence initiated' },
  { key: 'premium', label: 'Premium', price: '$1,000', desc: '50 executives · Narrow criteria · AI verification included' },
  { key: 'managed', label: 'Managed', price: '$2,500', desc: 'Custom list size · Full outreach · Executive vetting · Post draft review' },
];

export function CampaignBuilderTab() {
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [networkCount, setNetworkCount] = useState(0);
  const [newBrand, setNewBrand] = useState(false);
  const [form, setForm] = useState({
    brand_id: '', title: '', industry: '', platforms: [], brief: '',
    required_talking_points: [], prohibited_content: [], brand_guidelines: '',
    compensation_type: 'flat', flat_fee: '', cpm_rate: '', floor_guarantee: '',
    performance_bonus_threshold: '', performance_bonus_amount: '',
    exec_tier_required: 'any', min_followers: '', spots_total: 10, deadline: '',
    setup_fee_tier: 'standard',
    // New brand fields
    brand_name: '', brand_contact: '', brand_email: '', brand_website: '',
  });
  const [tagInput, setTagInput] = useState({ talking: '', prohibited: '' });

  useEffect(() => {
    axios.get(`${API}/brands/v2`, { headers: getAuthHeaders() }).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.industry) {
      axios.get(`${API}/network-count/${form.industry}`, { headers: getAuthHeaders() }).then(r => setNetworkCount(r.data.active_count || 0)).catch(() => {});
    }
  }, [form.industry]);

  const addTag = (field, value) => {
    if (value.trim()) setForm({...form, [field]: [...form[field], value.trim()]});
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      // Create brand first if new
      let brandId = form.brand_id;
      if (newBrand && form.brand_name) {
        const { data: brand } = await axios.post(`${API}/brands/v2`, {
          name: form.brand_name, contact_name: form.brand_contact, contact_email: form.brand_email,
          website: form.brand_website, industry: form.industry
        }, { headers: getAuthHeaders() });
        brandId = brand.id;
      }

      const tier = SETUP_TIERS.find(t => t.key === form.setup_fee_tier);
      const payload = {
        ...form, brand_id: brandId,
        flat_fee: form.flat_fee ? parseFloat(form.flat_fee) : null,
        cpm_rate: form.cpm_rate ? parseFloat(form.cpm_rate) : null,
        floor_guarantee: form.floor_guarantee ? parseFloat(form.floor_guarantee) : null,
        performance_bonus_threshold: form.performance_bonus_threshold ? parseInt(form.performance_bonus_threshold) : null,
        performance_bonus_amount: form.performance_bonus_amount ? parseFloat(form.performance_bonus_amount) : null,
        min_followers: form.min_followers ? parseInt(form.min_followers) : 0,
        spots_total: parseInt(form.spots_total) || 10,
        setup_fee_amount: tier ? parseFloat(tier.price.replace(/[^0-9.]/g, '')) : 500,
      };
      const { data: campaign } = await axios.post(`${API}/campaigns/v2`, payload, { headers: getAuthHeaders() });
      setResult(campaign);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create campaign');
    } finally { setCreating(false); }
  };

  const handlePayAndBuild = async () => {
    if (!result) return;
    try {
      // Mark fee as paid (manual)
      await axios.post(`${API}/campaigns/v2/${result.id}/setup-fee/pay`, { method: 'manual' }, { headers: getAuthHeaders() });
      // Build list
      const { data: buildResult } = await axios.post(`${API}/campaigns/v2/${result.id}/build-list`, {}, { headers: getAuthHeaders() });
      setResult({ ...result, build_result: buildResult, status: 'outreach' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const spotsNeeded = parseInt(form.spots_total) || 10;
  const estimatedNew = Math.max(0, spotsNeeded - networkCount);
  const coveragePercent = spotsNeeded > 0 ? Math.min(100, Math.round((networkCount / spotsNeeded) * 100)) : 0;

  if (result?.build_result) {
    const br = result.build_result;
    return (
      <div data-testid="build-result" className="max-w-xl mx-auto">
        <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-6 mb-4">
          <CheckCircle className="w-8 h-8 text-[#10B981] mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">List Built for {result.title}</h3>
          <p className="text-sm text-[#9CA3AF] mb-4">{br.total} executives selected</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#090E33] p-3"><span className="font-mono text-2xl text-[#10B981]">{br.from_existing_db}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1">From existing network — $0 cost</p></div>
            <div className="bg-[#090E33] p-3"><span className="font-mono text-2xl text-[#FF4343]">{br.new_acquisitions}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1">New acquisitions — ~{br.estimated_apollo_credits} credits</p></div>
          </div>
        </div>
        <button onClick={() => { setResult(null); setStep(1); setForm({...form, title: '', brief: ''}); }} className="text-xs text-[#FF4343] hover:underline">Create Another Campaign</button>
      </div>
    );
  }

  if (result) {
    return (
      <div data-testid="campaign-created" className="max-w-xl mx-auto">
        <div className="bg-[#111827] border border-white/10 p-6">
          <h3 className="font-display text-xl font-bold mb-2">Campaign Created</h3>
          <p className="text-sm text-[#9CA3AF] mb-4">{result.title}</p>
          <p className="text-xs text-[#6B7280] mb-4">Setup fee: <span className="font-mono text-[#FF4343]">${result.setup_fee_amount || 500}</span> ({result.setup_fee_tier})</p>
          <div className="flex gap-3">
            <button onClick={handlePayAndBuild} data-testid="pay-and-build-btn"
              className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-5 py-2 text-sm font-semibold transition-all">
              Mark Paid & Build List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="campaign-builder" className="max-w-3xl">
      {/* Progress */}
      <div className="flex gap-2 mb-6">
        {['Brand + Brief', 'Compensation', 'Setup Fee', 'Review'].map((s, i) => (
          <div key={i} className={`flex-1 text-center text-[10px] font-mono uppercase py-2 border-b-2 ${step === i + 1 ? 'border-[#FF4343] text-[#FF4343]' : step > i + 1 ? 'border-[#10B981] text-[#10B981]' : 'border-white/10 text-[#6B7280]'}`}>{s}</div>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-1">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setNewBrand(false)} className={`text-xs px-3 py-1 border ${!newBrand ? 'border-[#FF4343] text-[#FF4343]' : 'border-white/10 text-[#6B7280]'}`}>Existing Brand</button>
            <button onClick={() => setNewBrand(true)} className={`text-xs px-3 py-1 border ${newBrand ? 'border-[#FF4343] text-[#FF4343]' : 'border-white/10 text-[#6B7280]'}`}>New Brand</button>
          </div>
          {!newBrand ? (
            <select value={form.brand_id} onChange={e => setForm({...form, brand_id: e.target.value})} data-testid="brand-select"
              className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value="">Select Brand</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Brand Name" value={form.brand_name} onChange={e => setForm({...form, brand_name: e.target.value})} className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              <input placeholder="Contact Name" value={form.brand_contact} onChange={e => setForm({...form, brand_contact: e.target.value})} className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              <input placeholder="Contact Email" value={form.brand_email} onChange={e => setForm({...form, brand_email: e.target.value})} className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              <input placeholder="Website" value={form.brand_website} onChange={e => setForm({...form, brand_website: e.target.value})} className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
            </div>
          )}
          <input placeholder="Campaign Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="campaign-title"
            className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} data-testid="campaign-industry"
            className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
            <option value="">Select Industry</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <div className="flex flex-wrap gap-1.5">
            {['LinkedIn','X','Instagram','YouTube'].map(p => (
              <button key={p} onClick={() => setForm({...form, platforms: form.platforms.includes(p) ? form.platforms.filter(x => x !== p) : [...form.platforms, p]})}
                className={`text-xs px-3 py-1 border ${form.platforms.includes(p) ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>{p}</button>
            ))}
          </div>
          <textarea placeholder="Campaign brief..." value={form.brief} onChange={e => setForm({...form, brief: e.target.value})} rows={4} data-testid="campaign-brief"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
          {/* Tags */}
          <div>
            <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1 block">Required Talking Points</label>
            <div className="flex flex-wrap gap-1 mb-1">{form.required_talking_points.map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">{t} <button onClick={() => setForm({...form, required_talking_points: form.required_talking_points.filter((_, j) => j !== i)})}>&times;</button></span>)}</div>
            <input value={tagInput.talking} onChange={e => setTagInput({...tagInput, talking: e.target.value})} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag('required_talking_points', tagInput.talking); setTagInput({...tagInput, talking: ''}); }}}
              placeholder="Type + Enter" className="w-full px-3 py-1.5 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-2">
          <div className="flex gap-2">
            {['flat', 'cpm', 'hybrid'].map(t => (
              <button key={t} onClick={() => setForm({...form, compensation_type: t})}
                className={`text-xs px-4 py-2 border capitalize ${form.compensation_type === t ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>{t === 'flat' ? 'Flat Fee' : t === 'cpm' ? 'CPM' : 'Hybrid'}</button>
            ))}
          </div>
          {(form.compensation_type === 'flat' || form.compensation_type === 'hybrid') && (
            <input type="number" placeholder="Flat fee per post ($)" value={form.flat_fee} onChange={e => setForm({...form, flat_fee: e.target.value})}
              className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          )}
          {(form.compensation_type === 'cpm' || form.compensation_type === 'hybrid') && (
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="CPM rate ($)" value={form.cpm_rate} onChange={e => setForm({...form, cpm_rate: e.target.value})} className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              <input type="number" placeholder="Floor guarantee ($)" value={form.floor_guarantee} onChange={e => setForm({...form, floor_guarantee: e.target.value})} className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <select value={form.exec_tier_required} onChange={e => setForm({...form, exec_tier_required: e.target.value})}
              className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value="any">Any Tier</option>
              {TIERS.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </select>
            <input type="number" placeholder="Min followers" value={form.min_followers} onChange={e => setForm({...form, min_followers: e.target.value})}
              className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Total spots" value={form.spots_total} onChange={e => setForm({...form, spots_total: e.target.value})}
              className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
            <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})}
              className="px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
          </div>
        </div>
      )}

      {/* Step 3 — Setup Fee */}
      {step === 3 && (
        <div data-testid="step-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {SETUP_TIERS.map(t => (
              <button key={t.key} onClick={() => setForm({...form, setup_fee_tier: t.key})} data-testid={`tier-${t.key}`}
                className={`text-left p-5 border transition-all ${form.setup_fee_tier === t.key ? 'border-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 bg-[#111827] hover:border-white/20'}`}>
                <span className="text-xs font-mono uppercase tracking-wider text-[#6B7280]">{t.label}</span>
                <span className="block font-mono text-3xl font-bold text-[#FF4343] mt-1">{t.price}</span>
                <p className="text-[10px] text-[#6B7280] mt-2 leading-relaxed">{t.desc}</p>
              </button>
            ))}
          </div>
          {/* Network Counter */}
          {form.industry && (
            <div className="bg-[#111827] border border-white/10 p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-white">Executives already in our network for <span className="text-[#FF4343]">{form.industry}</span>:</span>
                <span className="font-mono text-3xl font-bold text-[#10B981]" data-testid="network-counter">{networkCount}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#9CA3AF]">Estimated new Apollo acquisitions needed:</span>
                <span className="font-mono text-2xl font-bold text-[#FF4343]">{estimatedNew}</span>
              </div>
              <div className="w-full h-2 bg-white/5 mb-2">
                <div className="h-full bg-[#10B981] transition-all" style={{ width: `${coveragePercent}%` }} />
              </div>
              {coveragePercent >= 80 && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 text-xs text-[#10B981]" data-testid="high-coverage-callout">
                  Most of this list will be filled from existing network. Apollo credit cost for this campaign is minimal.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 4 — Review */}
      {step === 4 && (
        <div data-testid="step-4" className="bg-[#111827] border border-white/10 p-6 space-y-3">
          <h3 className="font-display text-lg font-bold">Campaign Summary</h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <span className="text-[#6B7280]">Title:</span><span className="text-white">{form.title}</span>
            <span className="text-[#6B7280]">Industry:</span><span className="text-white">{form.industry}</span>
            <span className="text-[#6B7280]">Platforms:</span><span className="text-white">{form.platforms.join(', ')}</span>
            <span className="text-[#6B7280]">Compensation:</span><span className="text-white capitalize">{form.compensation_type}{form.flat_fee ? ` · $${form.flat_fee}` : ''}{form.cpm_rate ? ` · $${form.cpm_rate} CPM` : ''}</span>
            <span className="text-[#6B7280]">Spots:</span><span className="text-white">{form.spots_total}</span>
            <span className="text-[#6B7280]">Tier Required:</span><span className="text-white capitalize">{form.exec_tier_required}</span>
            <span className="text-[#6B7280]">Setup Fee:</span><span className="font-mono text-[#FF4343]">{SETUP_TIERS.find(t => t.key === form.setup_fee_tier)?.price} ({form.setup_fee_tier})</span>
            <span className="text-[#6B7280]">Network Available:</span><span className="text-[#10B981]">{networkCount} executives</span>
          </div>
        </div>
      )}

      {/* Nav */}
      <div className="flex justify-between mt-6">
        {step > 1 ? <button onClick={() => setStep(step - 1)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">Back</button> : <div />}
        {step < 4 ? (
          <button onClick={() => setStep(step + 1)} className="text-xs px-5 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all" data-testid="next-step-btn">Continue</button>
        ) : (
          <button onClick={handleCreate} disabled={creating} data-testid="launch-campaign-btn"
            className="text-xs px-6 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
            {creating ? 'Creating...' : 'Launch Campaign'}
          </button>
        )}
      </div>
    </div>
  );
}

// ==================== DASHBOARD STATS ====================
export function DashboardStatsCards() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }).then(r => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="dashboard-stats">
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 min-w-0">
        <Users className="w-5 h-5 text-[#FF4343] mb-2" />
        <span className="block font-mono text-xl sm:text-3xl font-bold text-white tabular-nums leading-tight truncate">{stats.network_size}</span>
        <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1 truncate">Active Network</p>
      </div>
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 min-w-0">
        <TrendingUp className="w-5 h-5 text-[#10B981] mb-2" />
        <span className="block font-mono text-xl sm:text-3xl font-bold text-[#10B981] tabular-nums leading-tight truncate">{stats.db_hit_rate}%</span>
        <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1 truncate">DB Hit Rate</p>
      </div>
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 min-w-0">
        <Briefcase className="w-5 h-5 text-[#FF4343] mb-2" />
        <span className="block font-mono text-xl sm:text-3xl font-bold text-white tabular-nums leading-tight truncate">{stats.active_campaigns}</span>
        <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1 truncate">Active Campaigns</p>
      </div>
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 min-w-0">
        <DollarSign className="w-5 h-5 text-[#FF4343] mb-2" />
        <span className="block font-mono text-xl sm:text-3xl font-bold text-[#FF4343] tabular-nums leading-tight truncate">${stats.setup_fees_mtd.toLocaleString()}</span>
        <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1 truncate">Setup Fees MTD</p>
      </div>
      {/* Industry Coverage */}
      {Object.keys(stats.industry_coverage || {}).length > 0 && (
        <div className="col-span-2 md:col-span-4 bg-[#111827] border border-white/10 p-5">
          <h4 className="text-xs font-mono uppercase text-[#6B7280] mb-3">Industry Coverage</h4>
          <div className="space-y-2">
            {Object.entries(stats.industry_coverage).sort((a, b) => b[1] - a[1]).map(([ind, count]) => (
              <div key={ind} className="flex items-center gap-3">
                <span className="text-xs text-[#9CA3AF] w-28 truncate">{ind}</span>
                <div className="flex-1 h-3 bg-white/5"><div className="h-full bg-[#FF4343]/60" style={{ width: `${Math.min(100, (count / Math.max(...Object.values(stats.industry_coverage))) * 100)}%` }} /></div>
                <span className="text-xs font-mono text-[#6B7280] w-8 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
